import cachedRequest from 'configs/cachedRequest';
import Settings from 'configs/Settings';
import { action } from 'mobx';
import { TEMPLATE } from 'modules/membership';

import {
  STREAMING,
  SPARKLE_COMMUNICATION_ENABLED,
  SPARKLE_COMMUNICATION_DISABLED
} from './constants';
import Store from './MembershipStore';

import packageJson from '../../../package.json';

import { setCustomConfig } from '@hotmart/club-files-ui';
import Request from '@hotmart/request';

/**
 * @class
 * This class manages all operations related to Membership, including
 * API requests and store management.
 * All required data (including dependency on data from other services) and its
 * own core data fetching should be dealt with in the `init` method.
 */
class MembershipService {
  wasInitialized = false;
  clubVersion = 'OLD_CLUB_ADMIN';
  migratedPages = [
    'content',
    'classManager',
    'ctaCommunities',
    'userManager',
    'broadcastManager',
    'community',
    'salesManager',
    'configManager',
    'reports',
    'dashboard'
  ];

  @action
  async init() {
    if (this.wasInitialized) {
      return;
    }

    if (!Store.loaded) {
      await this.fetchData();
    }

    this.wasInitialized = true;
  }

  setMembershipVersion() {
    this.clubVersion = 'MEMBERSHIP';
  }

  setMembershipId(membershipId) {
    this.membershipId = membershipId;
  }

  setSlug(slug) {
    this.slug = slug;
  }

  setUploadCustomConfig(membershipOwnerHash, streamingDrmProtection) {
    setCustomConfig({ name: 'membershipOwnerId', value: membershipOwnerHash });
    setCustomConfig({
      name: 'extraMetaData',
      value: {
        isMembershipDrmProtectionActive: streamingDrmProtection
      }
    });
  }

  setSubdomain(subdomain) {
    if (Store.subdomain !== subdomain) {
      this.wasInitialized = false;
      Store.loaded = false;
      Store.subdomain = subdomain;
    }

    Request.setDefaultHeaders({
      'Club': subdomain,
      'x-app-name': packageJson.name
    });
  }

  fetchData = async () => {
    try {
      const response = await cachedRequest(
        () => Request('membership.data', { attach_token: false }),
        ['membership.data', this.subdomain]
      );

      const { membershipOwnerHash, streamingDrmProtection } = response;

      this.setUploadCustomConfig(membershipOwnerHash, streamingDrmProtection);
      Object.assign(Store, { loaded: true, ...response });
    } catch (error) {
      const isNotFound =
        error && error.content && error.content.error === 'CLUB_AREA_NOT_FOUND';

      isNotFound && window.location.replace('https://www.hotmart.com/club/');
    }
    return void 0;
  };

  get domainUrl() {
    const protocol = Settings.isLocal ? 'http' : 'https';

    return `${protocol}://${this.subdomain}.${Settings.domain}`;
  }

  get academy() {
    return Store.academy;
  }

  get isStreamingFreeContentEnabled() {
    return Store.isStreamingFreeContentEnabled;
  }

  get isStreamingFreeEnabled() {
    return Store.isStreamingFreeEnabled;
  }

  get isStreamingEnabled() {
    return Store.isStreamingEnabled;
  }

  disableStreaming() {
    Store.configs = Store.configs.filter((conf) => conf !== STREAMING);
  }

  enableStreaming() {
    Store.configs.push(STREAMING);
  }

  setStreamingStatus(status) {
    status === true ? this.enableStreaming() : this.disableStreaming();
  }

  get isSparkleCommunicationEnabled() {
    return Store.isSparkleCommunicationEnabled;
  }

  setSparkleCommunicationStatus(active) {
    if (active) {
      this.enableSparkleCommunication();
    } else {
      this.disableSparkleCommunication();
    }
  }

  enableSparkleCommunication() {
    Store.configs = Store.configs.filter(
      (conf) => conf !== SPARKLE_COMMUNICATION_DISABLED
    );
    Store.configs.push(SPARKLE_COMMUNICATION_ENABLED);
  }

  disableSparkleCommunication() {
    Store.configs = Store.configs.filter(
      (conf) => conf !== SPARKLE_COMMUNICATION_ENABLED
    );
    Store.configs.push(SPARKLE_COMMUNICATION_DISABLED);
  }

  setStreamingDrmProtection(value) {
    Store.streamingDrmProtection = value;
    this.setUploadCustomConfig(Store.membershipOwnerHash, value);
  }

  get isStreamingCampaignCandidate() {
    return Store.isStreamingCampaignCandidate;
  }

  get currencyCode() {
    return Store.currencyCode;
  }

  get subdomain() {
    return Store.subdomain;
  }

  get template() {
    return Store.template;
  }

  set template(template) {
    Store.template = template;
  }

  set productHomePreviewUrl(value) {
    Store.productHomePreviewUrl = value;
  }

  get isMidnight() {
    return [TEMPLATE.MIDNIGHT, TEMPLATE.ONLINE_EVENT].includes(this.template);
  }

  get isOnlineEventTemplate() {
    return this.template === TEMPLATE.ONLINE_EVENT;
  }

  get hasSeason() {
    return Store.hasSeason;
  }

  get productId() {
    return Store.productId;
  }

  setForceOnlineCourseLabels(value) {
    Store.forceOnlineCourseLabels = value;
  }

  getLink(page = 'content') {
    return this.migratedPages.includes(page)
      ? Store.links[page]
      : `${this.domainUrl}/admin/pages${Store.links[page]}.html`;
  }

  redirectTo(page = 'content') {
    this.migratedPages.includes(page)
      ? window.h.push(Store.links[page])
      : (window.location = this.getLink(page));
  }

  @action
  visualize(role = 'OWNER', classHash, season = null) {
    let url = this.domainUrl;

    if (this.isMidnight) {
      if (Settings.isLocal) {
        url += ':8080';
      }
      url += '/t';
    } else {
      if (Settings.isLocal) {
        url += ':3000';
      }
      url += '/lesson';
    }

    url += `?role=${role}`;

    if (classHash) {
      url += `&classHash=${classHash}`;
    }

    if (season) {
      url += `&season=${season}`;
    }

    window.open(url);
  }
}

export default new MembershipService();
